// Cellery Soapbox — typed Firestore CRUD (CONTRACTS §4/§5/§6).
// Reads/writes go straight to the Web SDK and are gated by firestore.rules.
// Emails are lowercased before write. Live collections use onSnapshot.

import {
  collection,
  doc,
  getDoc as fbGetDoc,
  getDocs,
  limit as fbLimit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  type Timestamp,
  type Unsubscribe,
} from "firebase/firestore";
import { db } from "./firebase";
import { HIGHLIGHT_COLOR } from "./types";
import type {
  Anchor,
  Annotation,
  Comment,
  DocMeta,
  DocVersion,
  FolderMeta,
  ViewerStat,
  ViewSession,
} from "./types";

/** Live list of docs the signed-in user may view (owner is in `members`). */
export function listMyDocs(
  emailLower: string,
  onData: (docs: DocMeta[]) => void,
  onError?: (err: Error) => void
): Unsubscribe {
  const q = query(
    collection(db, "docs"),
    where("members", "array-contains", emailLower),
    orderBy("createdAt", "desc")
  );
  return onSnapshot(
    q,
    (snap) =>
      onData(snap.docs.map((d) => ({ ...(d.data() as DocMeta), docId: d.id }))),
    (err) => onError?.(err)
  );
}

/**
 * Live list of folders the signed-in user may see. Effective `members` (self +
 * inherited ancestor grants) is denormalized server-side, so this is the same
 * single array-contains query as docs. The workspace builds the tree client-side
 * from `parentId`.
 */
export function listMyFolders(
  emailLower: string,
  onData: (folders: FolderMeta[]) => void,
  onError?: (err: Error) => void
): Unsubscribe {
  const q = query(
    collection(db, "folders"),
    where("members", "array-contains", emailLower),
    orderBy("createdAt", "desc")
  );
  return onSnapshot(
    q,
    (snap) =>
      onData(
        snap.docs.map((d) => ({ ...(d.data() as FolderMeta), folderId: d.id }))
      ),
    (err) => onError?.(err)
  );
}

export async function getDoc(docId: string): Promise<DocMeta | null> {
  const snap = await fbGetDoc(doc(db, "docs", docId));
  return snap.exists()
    ? { ...(snap.data() as DocMeta), docId: snap.id }
    : null;
}

/**
 * Live subscription to a single doc's metadata. Used by the viewer to notice
 * when another editor updated the shared draft or published a new version.
 */
export function watchDoc(
  docId: string,
  onData: (meta: DocMeta | null) => void,
  onError?: (err: Error) => void
): Unsubscribe {
  return onSnapshot(
    doc(db, "docs", docId),
    (snap) =>
      onData(snap.exists() ? { ...(snap.data() as DocMeta), docId: snap.id } : null),
    (err) => onError?.(err)
  );
}

/** Versions are immutable; a one-shot read ordered oldest-first. */
export async function listVersions(docId: string): Promise<DocVersion[]> {
  const q = query(
    collection(db, "docs", docId, "versions"),
    orderBy("publishedAt", "asc")
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({
    ...(d.data() as DocVersion),
    versionId: d.id,
  }));
}

export async function getVersion(
  docId: string,
  versionId: string
): Promise<DocVersion | null> {
  const snap = await fbGetDoc(doc(db, "docs", docId, "versions", versionId));
  return snap.exists()
    ? { ...(snap.data() as DocVersion), versionId: snap.id }
    : null;
}

/** Live annotation list for a version, oldest-first. */
export function listAnnotations(
  docId: string,
  versionId: string,
  onData: (anns: Annotation[]) => void,
  onError?: (err: Error) => void
): Unsubscribe {
  const q = query(
    collection(db, "docs", docId, "versions", versionId, "annotations"),
    orderBy("createdAt", "asc")
  );
  return onSnapshot(
    q,
    (snap) =>
      onData(
        snap.docs.map((d) => ({ ...(d.data() as Annotation), annId: d.id }))
      ),
    (err) => onError?.(err)
  );
}

export async function createAnnotation(
  docId: string,
  versionId: string,
  anchor: Anchor,
  authorUid: string,
  authorEmail: string
): Promise<string> {
  const ref = doc(
    collection(db, "docs", docId, "versions", versionId, "annotations")
  );
  await setDoc(ref, {
    annId: ref.id,
    anchor,
    authorUid,
    authorEmail: authorEmail.toLowerCase(),
    color: HIGHLIGHT_COLOR,
    resolved: false,
    createdAt: serverTimestamp(),
  });
  return ref.id;
}

export async function setResolved(
  docId: string,
  versionId: string,
  annId: string,
  resolved: boolean
): Promise<void> {
  await updateDoc(
    doc(db, "docs", docId, "versions", versionId, "annotations", annId),
    { resolved }
  );
}

/** Live comment thread for an annotation, oldest-first. */
export function listComments(
  docId: string,
  versionId: string,
  annId: string,
  onData: (comments: Comment[]) => void,
  onError?: (err: Error) => void
): Unsubscribe {
  const q = query(
    collection(
      db,
      "docs",
      docId,
      "versions",
      versionId,
      "annotations",
      annId,
      "comments"
    ),
    orderBy("createdAt", "asc")
  );
  return onSnapshot(
    q,
    (snap) =>
      onData(
        snap.docs.map((d) => ({ ...(d.data() as Comment), commentId: d.id }))
      ),
    (err) => onError?.(err)
  );
}

export async function addComment(
  docId: string,
  versionId: string,
  annId: string,
  body: string,
  authorUid: string,
  authorEmail: string
): Promise<string> {
  const ref = doc(
    collection(
      db,
      "docs",
      docId,
      "versions",
      versionId,
      "annotations",
      annId,
      "comments"
    )
  );
  await setDoc(ref, {
    commentId: ref.id,
    body,
    authorUid,
    authorEmail: authorEmail.toLowerCase(),
    createdAt: serverTimestamp(),
  });
  return ref.id;
}

/** Format a Firestore Timestamp for display; pending writes show an ellipsis. */
export function formatTimestamp(ts: Timestamp | null | undefined): string {
  if (!ts) return "…";
  return ts.toDate().toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

// --- Usage stats (CONTRACTS §4) --------------------------------------------
// The AGGREGATE rollup rides on DocMeta.stats, so it needs no query here. These
// read the per-person detail, which firestore.rules gates to owner + editors
// (canEdit) — callers must only invoke them when the user can edit, or the read
// is rejected.

/** Per-account rollups for a doc, heaviest reader first. */
export async function listDocViewers(docId: string): Promise<ViewerStat[]> {
  const q = query(
    collection(db, "docs", docId, "viewers"),
    orderBy("lastViewedAt", "desc")
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ ...(d.data() as ViewerStat), uid: d.id }));
}

/** Most recent reading sessions for the activity timeline, newest first. */
export async function listViewSessions(
  docId: string,
  max = 50
): Promise<ViewSession[]> {
  const q = query(
    collection(db, "docs", docId, "views"),
    orderBy("startedAt", "desc"),
    fbLimit(max)
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({
    ...(d.data() as ViewSession),
    sessionId: d.id,
  }));
}

/** Compact duration for stat tiles: "just under a minute" reads as "<1m". */
export function formatDuration(ms: number | null | undefined): string {
  if (!ms || ms < 0) return "0m";
  const totalMinutes = Math.floor(ms / 60_000);
  if (totalMinutes < 1) return "<1m";
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours === 0) return `${minutes}m`;
  return minutes === 0 ? `${hours}h` : `${hours}h ${minutes}m`;
}

/** Relative "when", for card meta lines. Falls back to absolute past a week. */
export function formatRelative(ts: Timestamp | null | undefined): string {
  if (!ts) return "never";
  const then = ts.toDate();
  const seconds = Math.floor((Date.now() - then.getTime()) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days <= 7) return `${days}d ago`;
  return then.toLocaleDateString(undefined, { dateStyle: "medium" });
}
